import { Component, ElementRef, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { fabric } from 'fabric';
import { Canvas } from 'fabric/fabric-impl';

@Component({
    selector: 'app-image-trim',
    templateUrl: './image-trim.component.html',
    styleUrls: ['./image-trim.component.scss'],
    standalone: false
})
export class ImageTrimComponent implements OnInit {
  readonly HEIGHT = 800;
  readonly WIDTH = 800;

  @ViewChild('cropCanvas') canvasDom: ElementRef;

  public canvas: Canvas;
  public cropped = false;
  private image: any;
  private mouseDownEvent: MouseEvent;
  private lastMousePositionX : number;
  private lastMousePositionY : number;
  private rectangle = new fabric.Rect({
    fill: 'transparent',
    stroke: '#ccc',
    strokeDashArray: [2, 2],
    visible: false
  });

  private imageSrc: string;
  private imageScale: number;

  @Output() closePanel = new EventEmitter<any>();

  constructor(@Inject(MAT_DIALOG_DATA) public data: any) {
    this.imageSrc = data.src;
  }

  ngOnInit(): void {
    fabric.util.loadImage(this.imageSrc, this.loadImage.bind(this));
  }

  private loadImage(img: any) {
    this.image = new fabric.Image(img);
    this.image.selectable = false;

    this.setupCanvas();

    this.canvas.on('mouse:down', (event) => this.mouseDown(event));
    this.canvas.on('mouse:move', (event) => this.mouseMove(event));
    this.canvas.on('mouse:up', () => this.mouseUp());
  }

  private setupCanvas(): void {
    this.canvas = new fabric.Canvas('crop-canvas');
    this.canvas.preserveObjectStacking = true;
    this.canvas.freeDrawingBrush.width = 3;

    this.imageScale = this.getScale(this.WIDTH, this.HEIGHT, this.image.width, this.image.height);
    const imgConfig = {
      top: this.HEIGHT / 2,
      left: this.WIDTH / 2,
      originX: 'center',
      originY: 'center',
      scaleX: this.imageScale,
      scaleY: this.imageScale
    };

    fabric.Image.fromURL(this.imageSrc, (img) => {
      img.selectable = false;
      this.image = img;
      this.canvas.setWidth(img.width * this.imageScale);
      this.canvas.setHeight(img.height * this.imageScale);
      this.canvas.add(img);
      this.canvas.centerObject(img);
      this.canvas.add(this.rectangle);
      this.canvas.renderAll();
    }, imgConfig);
  }

  private getScale(cw: number, ch: number, iw: number, ih: number): number {
    const width = cw / iw;
    const height = ch / ih;
    return Math.min(width, height);
  }

  public mouseDown(event: any): void {
    this.mouseDownEvent = event.e;
    const pointer = this.canvas.getPointer(this.mouseDownEvent);

    this.rectangle.left = Math.floor(pointer.x);
    this.rectangle.top = Math.floor(pointer.y);
    this.rectangle.visible = true;

    this.canvas.bringToFront(this.rectangle);
    this.canvas.renderAll();
  }

  public mouseMove(event: any): void {
    if (!this.mouseDownEvent) { return; }
    const mouseMoveEvent: MouseEvent = event.e;
    const pointer = this.canvas.getPointer(mouseMoveEvent);
    this.lastMousePositionX = Math.floor(pointer.x);
    this.lastMousePositionY = Math.floor(pointer.y);
    this.rectangle.width = Math.abs(mouseMoveEvent.pageX - this.mouseDownEvent.pageX);
    this.rectangle.height = Math.abs(mouseMoveEvent.pageY - this.mouseDownEvent.pageY);
    this.canvas.renderAll();
  }

  public mouseUp(): void {
    this.verifyBoundariesAndRectanglePositions();
  }

  public isRectangleStarted(): boolean {
    return !(this.rectangle.top == this.lastMousePositionY || this.rectangle.left == this.lastMousePositionX || this.rectangle.width == 0 || this.rectangle.height == 0);
  }
  
  verifyBoundariesAndRectanglePositions() {

    if (!this.isRectangleStarted())
      return;

    if (this.lastMousePositionX < 0)
      this.lastMousePositionX = 0;
    if (this.lastMousePositionY < 0)
      this.lastMousePositionY = 0;

    const isRectangleStartedFromBottomRight = this.lastMousePositionX < this.rectangle.left && this.lastMousePositionY < this.rectangle.top;
    const isRectangleStartedFromBottomLeft = this.lastMousePositionY < this.rectangle.top; 
    const isRectangelStartedFromTopRight = this.lastMousePositionX < this.rectangle.left;

    if (isRectangleStartedFromBottomRight) { 
      this.rectangle.left = this.lastMousePositionX;
      this.rectangle.top = this.lastMousePositionY;
    } else if (isRectangleStartedFromBottomLeft) {
      this.rectangle.top = this.lastMousePositionY;
    } else if (isRectangelStartedFromTopRight) {
      this.rectangle.left = this .lastMousePositionX;
    }

    // Calculate right and bottom boundaries
    const rectangleRightPos = this.rectangle.left + this.rectangle.width;
    const rectangleBottomPos = this.rectangle.top + this.rectangle.height;
        
    // Adjust width and height if they go beyond the image boundaries on the right and bottom side
    this.rectangle.width = rectangleRightPos > this.canvas.width ? this.canvas.width - this.rectangle.left : this.rectangle.width;
    this.rectangle.height = rectangleBottomPos > this.canvas.height ? this.canvas.height - this.rectangle.top : this.rectangle.height;

    this.closePanel.emit(this.crop());
  }

  public crop(): any {
    // Create a data URL of the cropped area
    const imageSrc = this.canvas.toDataURL({ 
      left: this.rectangle.left,
      top: this.rectangle.top,
      width: this.rectangle.width,
      height: this.rectangle.height,
      quality: 1,
      format: 'jpeg'
    });

    return [imageSrc, this.rectangle];
  }
}
